import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import appsService from '../services/apps';
import {
    getToken,
    getIsPortalAdmin,
    resolveDefaultAppId,
    setSelectedAppId as persistSelectedAppId,
} from '../utils/storage';

const AppsContext = createContext(null);

export const useApps = () => {
    const context = useContext(AppsContext);
    if (!context) {
        throw new Error('useApps must be used within AppsProvider');
    }
    return context;
};

export const AppsProvider = ({ children }) => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // The app every app-scoped page works on; driven by the shell's app switcher.
    // Stays '' until the app list has loaded and a valid app has been resolved.
    const [selectedAppId, setSelectedAppIdState] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const urlAppId = searchParams.get('app_id') || '';
    const hasLoadedRef = useRef(false);
    const isLoadingRef = useRef(false);
    const initialLoadAttemptedRef = useRef(false);

    const loadApps = useCallback(async (forceRefresh = false) => {
        const token = getToken();
        if (!token) {
            hasLoadedRef.current = false;
            setApps([]);
            setLoading(false);
            return;
        }

        if (!forceRefresh && hasLoadedRef.current) return;
        if (isLoadingRef.current) return; // Prevent concurrent calls

        isLoadingRef.current = true;
        setLoading(true);
        setError('');
        try {
            // Portal admins can list every app; app-scoped users only their own.
            const data = getIsPortalAdmin() ? await appsService.getAll() : await appsService.getMine();
            setApps(data || []);
            hasLoadedRef.current = true;
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load apps');
            hasLoadedRef.current = false; // Reset on error so it can retry
        } finally {
            setLoading(false);
            isLoadingRef.current = false;
        }
    }, []);

    const refreshApps = useCallback(async () => {
        hasLoadedRef.current = false; // Reset ref to force refresh
        await loadApps(true);
    }, [loadApps]);

    // Expose a method to ensure apps are loaded (useful after login)
    // This will be called from ShellLayout when entering protected routes
    const ensureAppsLoaded = useCallback(() => {
        // Prevent duplicate calls
        if (initialLoadAttemptedRef.current || isLoadingRef.current || hasLoadedRef.current) {
            return;
        }

        initialLoadAttemptedRef.current = true;
        const token = getToken();
        if (token) {
            loadApps();
        } else {
            setLoading(false);
        }
    }, [loadApps]);

    // Forget the signed-in user's app list (logout) so the next login loads its own.
    const resetApps = useCallback(() => {
        hasLoadedRef.current = false;
        initialLoadAttemptedRef.current = false;
        setApps([]);
        setSelectedAppIdState('');
        setLoading(true);
        setError('');
    }, []);

    // Resolve the selected app: a valid ?app_id= (deep link) wins, then the current
    // selection if still in the list, then the persisted choice, then the first app.
    useEffect(() => {
        if (!apps.length) return;
        setSelectedAppIdState((current) => {
            if (urlAppId && apps.some((a) => a.app_id === urlAppId)) {
                persistSelectedAppId(urlAppId);
                return urlAppId;
            }
            if (current && apps.some((a) => a.app_id === current)) return current;
            const next = resolveDefaultAppId(apps);
            if (next) persistSelectedAppId(next);
            return next;
        });
    }, [apps, urlAppId]);

    // Switcher change: update context + storage, and the current URL's ?app_id so a
    // reload or shared link keeps the app. tenant_id belongs to the old app, so drop it.
    const setSelectedAppId = useCallback(
        (appId) => {
            setSelectedAppIdState(appId || '');
            persistSelectedAppId(appId || '');
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (appId) next.set('app_id', appId);
                    else next.delete('app_id');
                    next.delete('tenant_id');
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const selectedApp = useMemo(() => apps.find((a) => a.app_id === selectedAppId) || null, [apps, selectedAppId]);

    const value = {
        apps,
        loading,
        error,
        refreshApps,
        ensureAppsLoaded,
        resetApps,
        selectedAppId,
        selectedApp,
        setSelectedAppId,
    };

    return <AppsContext.Provider value={value}>{children}</AppsContext.Provider>;
};
